import { FlexBox, Icon, IconButton, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { type PropsWithChildren, useCallback, useRef, useState } from 'react';

import { defaultSectionOrder } from 'components/conversation/components/sidebar/conversationSidebar.types';
import { reorderArray } from 'components/conversation/components/sidebar/utils/reorder';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useOrderedDragDrop } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import styles from './cardSection.module.scss';

type CardSectionProps = PropsWithChildren & {
  index: number;
  title?: string;
  isMinimized?: boolean;
};

const type = Symbol('cardSection');

export const CardSection = ({
  title,
  children,
  index,
  isMinimized,
}: CardSectionProps): JSX.Element => {
  const flags = useFeatureFlag(['conversation-right-sidebar-visibility']);
  const storage = useServerStorage('user');
  const [isExpanded, setIsExpanded] = useState(true);
  const row = useRef<HTMLDivElement>(null);
  const rowHandle = useRef<HTMLDivElement>(null);

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarSectionOrder = reorderArray(
            storage.data.conversation?.sidebarSectionOrder,
            defaultSectionOrder,
            dragIndex,
            hoverIndex,
          );
        },
      });
    },
    [storage],
  );

  const { isOver } = useOrderedDragDrop({
    type,
    index,
    source: rowHandle,
    target: row,
    preview: row,
    onMove: handleMove,
    triggerSwap: 'drop',
  });

  return (
    <div
      ref={row}
      className={classNames(styles.root, {
        [styles.isOver]: isOver,
        [styles.minimized]: isMinimized,
      })}
    >
      {title && (
        <FlexBox alignItems="center" gap="300" padding="300 400 300 300">
          {flags.conversationRightSidebarVisibility && (
            <div ref={rowHandle} className={styles.handle}>
              <Icon name="bars" />
            </div>
          )}
          <FlexBox alignItems="center" justifyContent="space-between" flex="1">
            <Text.Paragraph variant="3" subdued padding="100 0">
              {title}
            </Text.Paragraph>
            {flags.conversationRightSidebarVisibility && (
              <IconButton
                onClick={() => setIsExpanded((prev) => !prev)}
                icon={isExpanded ? 'angle-down' : 'angle-right'}
                variant="tertiary"
              />
            )}
          </FlexBox>
        </FlexBox>
      )}
      {isExpanded && children}
    </div>
  );
};
